import React from 'react';
import { Link } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import {
  GripVertical,
  Pencil,
  Trash2,
  Clock,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import type { LearnSectionSummary } from '@/client/types/learn';
import { Button } from '@/components/ui/button';

interface SortableSectionRowProps {
  section: LearnSectionSummary;
  topicId: string;
  onDelete: (section: LearnSectionSummary) => void;
  onTogglePublish: (section: LearnSectionSummary) => void;
  isPublishing?: boolean;
}

export const SortableSectionRow: React.FC<SortableSectionRowProps> = ({
  section,
  topicId,
  onDelete,
  onTogglePublish,
  isPublishing,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.slug });

  const style: React.CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  const isPublished = Boolean(section.published);

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`border-b border-gray-100 hover:bg-gray-50/80 transition-colors ${
        isDragging ? 'bg-blue-50/60' : ''
      }`}
    >
      {/* Drag handle & order */}
      <td className="py-3.5 pl-4 pr-2 w-12 text-center">
        <div className="flex items-center gap-1.5 justify-center">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="cursor-grab text-gray-400 hover:text-gray-600 active:cursor-grabbing p-1 rounded touch-none"
            title="Drag to reorder prev/next"
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <span className="text-xs font-mono text-gray-400 w-4 text-center">
            {section.sort_order}
          </span>
        </div>
      </td>

      {/* Title & Slug */}
      <td className="py-3.5 px-4">
        <div className="flex flex-col">
          <Link
            to={`/admin/content/learn/sections/${encodeURIComponent(section.slug)}`}
            className="font-medium text-sm text-gray-900 hover:text-blue-600 transition-colors"
          >
            {section.title}
          </Link>
          <span className="text-xs font-mono text-gray-400">/learn/{section.slug}</span>
          {section.summary && (
            <span className="text-xs text-gray-500 line-clamp-1 mt-0.5 max-w-md">
              {section.summary}
            </span>
          )}
        </div>
      </td>

      {/* Reading Time */}
      <td className="py-3.5 px-4 text-xs text-gray-500 whitespace-nowrap">
        <div className="flex items-center gap-1">
          <Clock className="h-3.5 w-3.5 text-gray-400" />
          <span>{section.minutes ? `${section.minutes} min` : '—'}</span>
        </div>
      </td>

      {/* Publish Status Toggle */}
      <td className="py-3.5 px-4 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={isPublished}
              onChange={() => onTogglePublish(section)}
              disabled={isPublishing}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
          <span className={`text-xs font-medium ${isPublished ? 'text-emerald-700' : 'text-gray-500'}`}>
            {isPublished ? 'Published' : 'Draft'}
          </span>
          {isPublishing && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
        </div>
      </td>

      {/* Actions */}
      <td className="py-3.5 pr-4 pl-2 text-right whitespace-nowrap">
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" asChild className="h-8 text-xs text-gray-600 hover:text-blue-600">
            <Link to={`/admin/content/learn/sections/${encodeURIComponent(section.slug)}`}>
              <Pencil className="mr-1 h-3.5 w-3.5" />
              Edit
            </Link>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            asChild
            className="h-8 text-xs text-gray-500 hover:text-gray-700"
          >
            <a href={`/learn/${section.slug}`} target="_blank" rel="noreferrer">
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(section)}
            className="h-8 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
};
